/**
 * 授業モード ext 同期ヘルパー (v1)
 *
 * Teacher → Firestore → Student の経路で ext コンポーネントの内部状態を伝搬させる。
 * - syncExt(extId, state): Teacher が state を store に書き込み（Firestore 反映は authSetup.ts が監視）
 * - watchExtSync(extId, cb): Student が state 変更を購読
 * - createLessonSyncMixin: 簡易ミックスイン（Teacher 側 watch → Student 側反映を1行で）
 *
 * Teacher/Student 判定:
 * - Teacher = store.state.activeLesson?.syncEnabled === true
 * - Student = store.state.lessonMode?.active && syncEnabled === true
 */

import store from '@/store'

type AnyObj = Record<string, unknown>
// Vuex の state は `createStore({ state: {...} })` で推論されるが、
// 動的プロパティ (activeLesson / lessonMode / syncedExtState など authSetup.ts が直接代入) を含むため any で扱う
/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * 授業モード同期に対応している ext の `item.ext.name` 値レジストリ。
 * レイヤー一覧 UI（Dialog-layer.vue）で「同期可」バッジを出すために使用。
 * 実装済み ext の Vue `name:` フィールド (= layer-list.json の `ext.name`) を列挙。
 * 追加時はこの Set にも反映する。
 */
export const SYNC_CAPABLE_EXT_NAMES: ReadonlySet<string> = new Set([
  // 海陸段彩・陰影・DEM
  'extHypsometric', 'extBlendHillshade', 'extBlend',
  'extDemHillshade', 'extDemTint', 'extDemSlope', 'extDemAspect',
  'extDemCurvature', 'extDemLandform', 'extDemFlow',
  // 気象・時間軸
  'extRainRadar', 'extHimawari', 'extSentinel',
  // チョロプレス・統計
  'extColoring', 'extColoringChibanzu2025', 'extColoringChibanzu2026',
  'extChoropleth', 'extSchoolChoropleth',
  'ext-heatmap', 'ext-price-heatmap',
  // その他
  'extPlateau', 'extKkChipFilter', 'extSuikeiSlider',
  'extAmedas', 'extPointcloud',
])

/**
 * 指定された ext.name（layer-list.json の `ext.name` 値）が授業モード同期に対応しているか。
 */
export function isExtSyncCapable(extName: string | undefined | null): boolean {
  return !!extName && SYNC_CAPABLE_EXT_NAMES.has(extName)
}

function isTeacherSync(): boolean {
  return !!(store.state as any).activeLesson?.syncEnabled
}

function isStudentSync(): boolean {
  const lm = (store.state as any).lessonMode
  return !!(lm && lm.active && lm.syncEnabled)
}

/**
 * Teacher 側: ext の state を store に登録 → 既存の Teacher watcher が Firestore に送信
 * Teacher 以外のとき（生徒・未ログイン・通常モード）は no-op。
 */
export function syncExt(extId: string, state: AnyObj): void {
  if (!isTeacherSync()) return
  const s = store.state as any
  if (!s.syncedExtState) s.syncedExtState = {}
  // Firestore 送信可能な形式に深クローン（undefined/関数を除去）
  s.syncedExtState[extId] = JSON.parse(JSON.stringify(state))
}

/**
 * Student 側: syncedExtState[extId] の変更を購読。Student モードでないときはコールバック呼ばない。
 * immediate: true — 購読開始時に現在値があればすぐ適用（後入場の生徒でも追従）。
 * @returns unwatch 関数
 */
export function watchExtSync(
  extId: string,
  callback: (state: AnyObj) => void
): () => void {
  return store.watch(
    (state: any) => state.syncedExtState?.[extId],
    (newState: any) => {
      if (!isStudentSync()) return
      if (newState) callback(newState as AnyObj)
    },
    { deep: true, immediate: true }
  )
}

/**
 * 簡易ミックスイン: Teacher 側 data watch → syncExt、Student 側 watchExtSync → data 逆流入。
 *
 * 使い方:
 *   mixins: [createLessonSyncMixin({
 *     extId: 'ext-dem-tint',
 *     keys: ['ratio', 'palette'],
 *     onApply() { this.applyToMap() }  // Student 受信後の副作用（省略可）
 *   })]
 *
 * 注意: data に同じ名前のプロパティが存在する ext で使用すること。
 * computed (setter 付き) でも可だが、store 経由のバインディングだと deep watch が効かない場合があるので、
 * その場合は低レベル API (syncExt / watchExtSync) を直接使う。
 */
export function createLessonSyncMixin(config: {
  extId: string
  keys: string[]
  onApply?: (this: any) => void // eslint-disable-line @typescript-eslint/no-explicit-any
}) {
  const { extId, keys, onApply } = config

  const watchDefs: AnyObj = {}
  keys.forEach((k) => {
    watchDefs[k] = {
      handler(this: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
        if (!isTeacherSync()) return
        const payload: AnyObj = {}
        keys.forEach((kk) => { payload[kk] = this[kk] })
        syncExt(extId, payload)
      },
      deep: true,
    }
  })

  return {
    watch: watchDefs,
    mounted(this: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      this._lessonSyncUnwatch = watchExtSync(extId, (state) => {
        let changed = false
        keys.forEach((k) => {
          if (state[k] !== undefined && this[k] !== state[k]) {
            this[k] = state[k]
            changed = true
          }
        })
        if (changed && typeof onApply === 'function') {
          onApply.call(this)
        }
      })
    },
    beforeUnmount(this: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      if (this._lessonSyncUnwatch) {
        this._lessonSyncUnwatch()
        this._lessonSyncUnwatch = null
      }
    },
  }
}
